import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { LogManager } from '@src/utility/Logger';
import { useCallback, useMemo, useRef, useState } from 'react';

import { VideoDeletionService } from '../../services/VideoDeletionService';

import type { OtherVideo } from '@src/types.ts/story';

const logger = LogManager.getLogger('content:useDeleteClips');

export type DeleteClipsResult = { deleted: number; failed: number };

type UseDeleteClipsReturn = {
  deleteClips: (clips: OtherVideo[]) => Promise<DeleteClipsResult>;
  isDeleting: boolean;
};

/**
 * Deletes the selected Video Clips as a group (PLAT-11118), in a single write.
 *
 * Deliberately not a loop over the card's own single-clip delete. That path is
 * a read-modify-write on the story per clip — three round trips each, and two
 * of them overlapping would put a deleted clip back — so a selection of fifteen
 * cost forty-five serialized requests to stay correct. One write over the whole
 * set is both faster and race-free, and it is the same field on the same record
 * either way. See `VideoDeletionService.deleteVideos`.
 *
 * The selection is not cleared here: `SocialClipsView` already prunes deleted
 * ids, and on a failure nothing was deleted, so the selection is intact to
 * retry with no special handling.
 */
export function useDeleteClips(): UseDeleteClipsReturn {
  const videoCreator = useVideoCreatorStore();

  const [isDeleting, setIsDeleting] = useState(false);
  const deletingRef = useRef(false);

  const deletionService = useMemo(
    () => new VideoDeletionService({ videoCreator }),
    [videoCreator],
  );

  const deleteClips = useCallback(
    async (clips: OtherVideo[]): Promise<DeleteClipsResult> => {
      if (deletingRef.current) return { deleted: 0, failed: 0 };

      const ids = clips.map(clip => clip.id).filter((id): id is string => !!id);

      deletingRef.current = true;
      setIsDeleting(true);

      try {
        logger.info('deleteClips: starting', {
          clipCount: ids.length,
          storyId: videoCreator.story?.id,
        });

        const result = await deletionService.deleteVideos(ids);

        if (!result.ok) {
          // Nothing was deleted — the write is atomic — so the whole selection
          // is still there and still picked.
          videoCreator.showToast({
            state: 'error',
            message: 'Could not delete the clips. Please try again.',
          });
          logger.info('deleteClips: complete', {
            deleted: 0,
            failed: ids.length,
          });
          return { deleted: 0, failed: ids.length };
        }

        videoCreator.showToast({
          state: 'success',
          message: `Deleted ${ids.length} clip${ids.length === 1 ? '' : 's'}.`,
        });
        logger.info('deleteClips: complete', { deleted: ids.length });
        return { deleted: ids.length, failed: 0 };
      } finally {
        deletingRef.current = false;
        setIsDeleting(false);
      }
    },
    [videoCreator, deletionService],
  );

  return { deleteClips, isDeleting };
}
